/**
 * <biosphere-dialog> — wiring for the native <dialog> rendered by
 * snippets/biosphere-dialog.liquid.
 *
 * The platform owns the hard parts: focus trap, ESC, ::backdrop, focus
 * return to the invoker. This element only connects the trigger, the close
 * button, and backdrop clicks, and re-dispatches open/close as bubbling
 * events (the native dialog `close` event does not bubble).
 */
class BiosphereDialog extends HTMLElement {
  #trigger;
  #dialog;
  #abort;

  connectedCallback() {
    this.#trigger = this.querySelector("[data-trigger]");
    this.#dialog = this.querySelector("dialog");
    if (!this.#trigger || !this.#dialog) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#trigger.addEventListener(
      "click",
      (event) => {
        // The trigger may be the no-JS fallback link; once enhanced, it
        // opens the dialog instead of navigating.
        event.preventDefault();
        this.#dialog.showModal();
        this.#dispatch("open");
      },
      { signal },
    );

    for (const closer of this.querySelectorAll("[data-close]")) {
      closer.addEventListener("click", () => this.#dialog.close(), { signal });
    }

    // Backdrop click: a click whose target is the <dialog> itself landed on
    // the backdrop, because the dialog's own box is fully covered by its
    // children (it has zero padding — see biosphere-dialog.css).
    this.#dialog.addEventListener(
      "click",
      (event) => {
        if (event.target === this.#dialog) this.#dialog.close();
      },
      { signal },
    );

    this.#dialog.addEventListener("close", () => this.#dispatch("close"), {
      signal,
    });

    this.#trigger.hidden = false;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #dispatch(name) {
    this.dispatchEvent(
      new CustomEvent(`biosphere:dialog:${name}`, { bubbles: true }),
    );
  }
}

if (!customElements.get("biosphere-dialog")) {
  customElements.define("biosphere-dialog", BiosphereDialog);
}
